import { _ } from 'wgnet-awesomejs';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import CryptoJS from 'crypto-js';

import BaseInput from 'source/lib/component/input/base';

import template from './template.html';

const FingerprintInput = BaseInput.extend({
    name: 'fingerprint',
    template,
    initialize(args = {}) {
        BaseInput.prototype.initialize.call(this, Object.assign(
            args,
            {
            }
        ));

        this.delegateEvents();
        this.generateFingerprint();
    },
    async generateFingerprint() {
        const fp = await FingerprintJS.load();
        const result = await fp.get();

        const stableComponentNames = [
            'platform', // ОС
            'timezone', // Часовой пояс
            'hardwareConcurrency' // Количество ядер CPU
        ];

        const stableComponents = {};
        _.each(stableComponentNames, (name) => {
            const component = result.components[name];
            if (component && component.value !== undefined) {
                let normalizedValue = component.value;

                if (name === 'hardwareConcurrency') {
                    normalizedValue = Math.floor(Number(normalizedValue) || 0);
                } else if (name === 'platform') {
                    normalizedValue = String(normalizedValue)
                        .toLowerCase()
                        .trim()
                        .replace(/\s+/g, ' ');
                } else if (name === 'timezone') {
                    normalizedValue = String(normalizedValue).trim();
                }

                if (normalizedValue !== null && normalizedValue !== undefined && normalizedValue !== 'language') {
                    stableComponents[name] = normalizedValue;
                }
            }
        });

        const additionalData = {
            canvas: this.getStableCanvasFingerprint()
        };

        const fingerprintData = {
            ...stableComponents,
            ...additionalData
        };

        const fingerprint = this.createFingerprintHash(fingerprintData);
        const encryptedFingerprint = this.encryptFingerprint(fingerprint);

        // Сохраняем fingerprint глобально для использования в HTTP-заголовке при отправке формы
        window.WGNI_Signature = encryptedFingerprint;
    },

    // Текущий canvas fingerprint включает:
    // 100x100 квадрат с фигурами:
    // - 9 прямоугольников (базовые цвета, оттенки, смешанные цвета)
    // - линейный градиент поверх части области
    // - 1 прямоугольник с тенью
    // - 3 линии
    // - 3 круга
    // - текст из трёх символов (O, A, B) разными шрифтами и размерами
    // - небольшой паттерн из 4‑цветного тайла
    // - область с режимом смешивания globalCompositeOperation = 'multiply'
    //
    // Базовый набор выборок:
    // - 18 точек по прямоугольникам (базовые, оттенки, смешанные цвета)
    // - 3 точки по области буквы O
    //   Итого: 21 точка выборки.
    //
    // Дополнительные наборы (можно отключать, просто закомментировав блоки ниже):
    // - 3 точки для градиента
    // - 3 точки для прямоугольника с тенью
    // - 2 точки для букв A и B
    getStableCanvasFingerprint() {
        const canvas = document.createElement('canvas');
        canvas.width = 100;
        canvas.height = 100;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            return 'no-canvas';
        }

        // Прямоугольники с базовыми цветами
        ctx.fillStyle = '#FF0000'; // Красный
        ctx.fillRect(10, 10, 20, 20);

        ctx.fillStyle = '#00FF00'; // Зеленый
        ctx.fillRect(40, 10, 20, 20);

        ctx.fillStyle = '#0000FF'; // Синий
        ctx.fillRect(70, 10, 20, 20);

        // Прямоугольники с оттенками
        ctx.fillStyle = '#FF8080'; // Светло-красный
        ctx.fillRect(10, 40, 20, 20);

        ctx.fillStyle = '#80FF80'; // Светло-зеленый
        ctx.fillRect(40, 40, 20, 20);

        ctx.fillStyle = '#8080FF'; // Светло-синий
        ctx.fillRect(70, 40, 20, 20);

        // Прямоугольники со смешанными цветами (rgba)
        ctx.fillStyle = 'rgba(255, 128, 0, 0.8)'; // Оранжевый с прозрачностью
        ctx.fillRect(10, 70, 20, 20);

        ctx.fillStyle = 'rgba(128, 0, 255, 0.8)'; // Фиолетовый с прозрачностью
        ctx.fillRect(40, 70, 20, 20);

        ctx.fillStyle = 'rgba(0, 255, 255, 0.8)'; // Голубой с прозрачностью
        ctx.fillRect(70, 70, 20, 20);

        // Линии с целочисленными координатами
        ctx.strokeStyle = '#FF0000';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(10, 50);
        ctx.lineTo(30, 70);
        ctx.stroke();

        ctx.strokeStyle = '#00FF00';
        ctx.beginPath();
        ctx.moveTo(40, 50);
        ctx.lineTo(60, 70);
        ctx.stroke();

        ctx.strokeStyle = '#0000FF';
        ctx.beginPath();
        ctx.moveTo(70, 50);
        ctx.lineTo(90, 70);
        ctx.stroke();

        // Круги (дуги) с целочисленными координатами
        ctx.fillStyle = '#FF0000';
        ctx.beginPath();
        ctx.arc(20, 85, 8, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = '#00FF00';
        ctx.beginPath();
        ctx.arc(50, 85, 8, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = '#0000FF';
        ctx.beginPath();
        ctx.arc(80, 85, 8, 0, Math.PI * 2);
        ctx.fill();

        // ОПЦИОНАЛЬНЫЕ УЛУЧШЕНИЯ
        // 1. Градиенты
        const linearGradient = ctx.createLinearGradient(20, 20, 80, 80);
        linearGradient.addColorStop(0, '#FF0000');
        linearGradient.addColorStop(0.5, '#00FF00');
        linearGradient.addColorStop(1, '#0000FF');
        ctx.fillStyle = linearGradient;
        ctx.fillRect(5, 5, 40, 40);

        // 2. Тени
        ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
        ctx.shadowBlur = 5;
        ctx.shadowOffsetX = 3;
        ctx.shadowOffsetY = 3;
        ctx.fillStyle = '#FF8800';
        ctx.fillRect(55, 5, 40, 40);
        ctx.shadowColor = 'transparent';

        // 3. Кривые Безье
        ctx.beginPath();
        ctx.moveTo(20, 60);
        ctx.bezierCurveTo(40, 40, 60, 80, 80, 60);
        ctx.strokeStyle = '#FF00FF';
        ctx.lineWidth = 2;
        ctx.stroke();

        // 4. Текст (символы O, A, B) с разными шрифтами и размерами
        ctx.fillStyle = '#000000';
        ctx.textBaseline = 'top';
        ctx.textAlign = 'center';

        // Буква O в центре верхней части
        ctx.font = '20px Arial'; // Фиксированный шрифт и размер
        ctx.fillText('O', 50, 5);

        // Буквы A и B по краям
        ctx.font = '12px "Times New Roman", serif';
        ctx.fillText('A', 15, 15);
        ctx.font = 'bold 14px Arial, sans-serif';
        ctx.fillText('B', 85, 15);

        // 5. Паттерны
        const patternCanvas = document.createElement('canvas');
        patternCanvas.width = 4;
        patternCanvas.height = 4;
        const patternCtx = patternCanvas.getContext('2d');
        patternCtx.fillStyle = '#FF0000';
        patternCtx.fillRect(0, 0, 2, 2);
        patternCtx.fillStyle = '#00FF00';
        patternCtx.fillRect(2, 0, 2, 2);
        patternCtx.fillStyle = '#0000FF';
        patternCtx.fillRect(0, 2, 2, 2);
        patternCtx.fillStyle = '#FFFF00';
        patternCtx.fillRect(2, 2, 2, 2);
        const pattern = ctx.createPattern(patternCanvas, 'repeat');
        ctx.fillStyle = pattern;
        ctx.fillRect(5, 55, 30, 30);

        // 6. Составные операции
        ctx.globalCompositeOperation = 'multiply';
        ctx.fillStyle = 'rgba(255, 0, 0, 0.3)';
        ctx.fillRect(65, 55, 30, 30);
        ctx.globalCompositeOperation = 'source-over';
        // === КОНЕЦ ОПЦИОНАЛЬНЫХ УЛУЧШЕНИЙ ===

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const { data } = imageData;

        // Нормализация цветов с большим количеством уровней для уникальности
        // но достаточно грубая для стабильности между браузерами
        const normalizeColor = (value) => {
            if (value < 32) return 0; // Черный
            if (value < 64) return 32; // Темно-серый
            if (value < 96) return 64; // Серый
            if (value < 128) return 96; // Светло-серый
            if (value < 160) return 128; // Средний
            if (value < 192) return 160; // Светлый
            if (value < 224) return 192; // Очень светлый
            return 255; // Белый
        };

        // Базовый набор: берём пиксели из всех областей с прямоугольниками
        // (базовые, оттенки и смешанные цвета),
        // а также 3 точки из области буквы O (всего 21 точка)
        const sample = [];
        const sampleRegions = [
            // Базовые цвета (верхний ряд)
            { x: 15, y: 15 }, // Красный
            { x: 20, y: 20 },
            { x: 45, y: 15 }, // Зеленый
            { x: 50, y: 20 },
            { x: 75, y: 15 }, // Синий
            { x: 80, y: 20 },
            // Оттенки (средний ряд)
            { x: 15, y: 45 }, // Светло-красный
            { x: 20, y: 50 },
            { x: 45, y: 45 }, // Светло-зеленый
            { x: 50, y: 50 },
            { x: 75, y: 45 }, // Светло-синий
            { x: 80, y: 50 },
            // Смешанные цвета (нижний ряд)
            { x: 15, y: 75 }, // Оранжевый
            { x: 20, y: 80 },
            { x: 45, y: 75 }, // Фиолетовый
            { x: 50, y: 80 },
            { x: 75, y: 75 }, // Голубой
            { x: 80, y: 80 },
            // Буква O (эксперимент)
            { x: 48, y: 10 }, // Центр буквы O
            { x: 50, y: 15 },
            { x: 52, y: 20 }
        ];

        // Опциональные точки выборки ===
        // Градиент: 3 дополнительные точки внутри области градиента
        sampleRegions.push(
            { x: 25, y: 10 },
            { x: 30, y: 25 },
            { x: 40, y: 35 },
        );

        // Тень: 3 дополнительные точки внутри прямоугольника с тенью
        sampleRegions.push(
            { x: 60, y: 10 },
            { x: 70, y: 20 },
            { x: 80, y: 30 },
        );

        // Текст A и B: 2 дополнительные точки в областях букв
        sampleRegions.push(
            { x: 17, y: 20 }, // Область буквы A
            { x: 83, y: 20 }, // Область буквы B
        );

        sampleRegions.forEach((region) => {
            const index = ((region.y * canvas.width) + region.x) * 4;
            if (index < data.length) {
                const normalizedColor = normalizeColor(data[index]);
                sample.push(normalizedColor);
            }
        });

        if (sample.length === 0) {
            return '0';
        }

        let hash = 0;
        for (let i = 0; i < sample.length; i += 1) {
            const multipliedHash = hash * 32;
            const subtractedHash = multipliedHash - hash;
            const addedHash = subtractedHash + sample[i];
            hash = addedHash % 2147483647;
        }

        return String(Math.abs(hash));
    },

    createFingerprintHash(data) {
        const valueToString = (val, path = '') => {
            if (val === null || val === undefined) {
                return '';
            }
            if (_.isArray(val)) {
                return val.sort().join(',');
            }
            if (_.isObject(val)) {
                const sortedKeys = _.keys(val).sort();
                return sortedKeys.map((k) => {
                    const nestedValue = valueToString(val[k], `${path}.${k}`);
                    return `${k}:${nestedValue}`;
                }).join('|');
            }
            return String(val);
        };

        const sortedKeys = _.keys(data).sort();

        const dataString = _.reduce(sortedKeys, (result, key) => {
            const value = data[key];
            const stringValue = valueToString(value, key);
            return `${result}${key}:${stringValue}|`;
        }, '');

        return dataString.toString().replace(/\|$/, '');
    },

    encryptFingerprint(hash) {
        return CryptoJS.MD5(hash).toString();
    },
});

export default FingerprintInput;
